// src/App.js
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { useEffect, useState } from "react";
import mqtt from "mqtt";

// Pages
import ImageGallery from "./pages/ImageGallery";
import FullscreenSlideshow from "./pages/Slideshow";
import MediaUploader from "./pages/MediaUploader";
import CartPage from "./pages/CartPage";
import AdminPage from "./pages/Admin";
import ItemsCatalog from "./pages/ItemsCatalog";
import VehiclesCatalog from "./pages/VehiclesCatalog";
import PackagesCatalog from "./pages/PackagesCatalog";
import Home from "./pages/Home";
import DogTags from "./pages/DogTags";
import WaiverForm from "./pages/WaiverForm";
import VehicleMap from "./pages/VehicleMap";
import TankJumpingGame from "./pages/TankJumpingGame";

// Components
import GunScreensaver from "./components/ItemsCatalog/GunScreensaver";
import GunScreensaverV2 from "./components/ItemsCatalog/GunScreensaverV2";
import Navigation from "./components/App/navigation_component";
import GlobalQR from "./components/Common/GlobalQR";
import ScrollToTopButton from "./components/App/scroll_button_component";
import BackButton from "./components/App/back_button_component";
import Footer from "./components/App/footer_component";

// Context
import { CartProvider } from "./context/CartContext";

// Config
import { MQTT_CONFIG } from "./configs/mqtt_config";

// Styles
import "./App.css";

function AppContent() {
  const location = useLocation();
  const isSlideshow = location.pathname === "/slideshow";
  const isGunScreensaver = location.pathname === "/gun-screensaver";
  const isGunScreensaverV2 = location.pathname === "/gun-screensaver-v2";
  const [isMobile, setIsMobile] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => setIsMobile(window.innerWidth <= 768);
    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    const handleScroll = () => {
      setShowScrollButton(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("resize", checkIfMobile);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      {!isSlideshow && !isGunScreensaver && !isGunScreensaverV2 && <Navigation />}
      <GlobalQR size={180} /> {/*  ← always visible & auto‑updates */}
      <ScrollToTopButton show={isMobile && !isSlideshow && !isGunScreensaver && !isGunScreensaverV2 && showScrollButton} />
      <BackButton
        show={!isMobile && !isSlideshow && !isGunScreensaver && !isGunScreensaverV2 && location.pathname !== "/"}
      />
      <div className={`main-content ${isSlideshow || isGunScreensaver || isGunScreensaverV2 ? "slideshow" : ""}`}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/gun-screensaver" element={<GunScreensaver />} />
          <Route path="/gun-screensaver-v2" element={<GunScreensaverV2 />} />
          <Route path="/image-gallery" element={<ImageGallery />} />
          <Route path="/upload" element={<MediaUploader />} />
          <Route path="/slideshow" element={<FullscreenSlideshow />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/items-catalog" element={<ItemsCatalog />} />
          <Route path="/vehicles" element={<VehiclesCatalog />} />
          <Route path="/packages" element={<PackagesCatalog />} />
          <Route path="/dogtags" element={<DogTags />} />
          <Route path="/waiver" element={<WaiverForm />} />
          <Route path="/map" element={<VehicleMap />} />
          <Route path="/tank-jumping" element={<TankJumpingGame />} />
        </Routes>

        {!isGunScreensaver && !isGunScreensaverV2 && <Footer />}
      </div>
    </>
  );
}

function App() {
  useEffect(() => {
    // Check for valid admin auth
    const authData = localStorage.getItem('uploadAuth');
    if (authData) {
      try {
        const { timestamp } = JSON.parse(authData);
        const now = Date.now();
        const twentyFourHours = 24 * 60 * 60 * 1000;
        
        if (now - timestamp < twentyFourHours) {
          const url = new URL(window.location.href);
          if (!url.searchParams.has('admin')) {
            url.searchParams.set('admin', 'true');
            window.location.href = url.toString();
            return; // Exit early since we're reloading
          }
        }
      } catch (e) {
        console.error('Error parsing uploadAuth:', e);
      }
    }

    // MQTT Client setup
    const client = mqtt.connect(MQTT_CONFIG.brokerUrl, {
      username: MQTT_CONFIG.username,
      password: MQTT_CONFIG.password,
      clientId: MQTT_CONFIG.clientId,
    });

    client.on("connect", () => {
      console.log("MQTT connected");
      client.subscribe(MQTT_CONFIG.topics, (err) => {
        if (err) {
          console.error("MQTT subscription error:", err);
        }
      });
    });

    client.on("message", (topic, message) => {
      if (topic === "refresh_page" && message.toString() === "true") {
        window.location.reload();
      }
    });

    client.on("error", (err) => {
      console.error("MQTT error:", err);
    });

    return () => {
      client.end();
    };
  }, []);

  return (
    <CartProvider>
      <Router>
        <AppContent />
      </Router>
    </CartProvider>
  );
}

export default App;
